interface WebinarROISectionProps {
  onRegister: () => void;
}

export default function WebinarROISection({ onRegister }: WebinarROISectionProps) {
  return (
    <section className="bg-alt-bg py-14 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-14">
          {/* Kicker — mirrors home page StatsSection */}
          <p className="text-[#4a5568] text-sm italic mb-4 leading-relaxed">
            Most title companies don&apos;t lose because they&apos;re bad — they lose because they&apos;re late.
          </p>

          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 mb-5">
            <i className="ri-bar-chart-2-line text-primary text-xs" />
            <span className="text-xs font-semibold text-navy">Why This Matters</span>
          </div>
          <h2
            className="text-navy font-bold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            Why This Matters
          </h2>

          {/* Mobile — stacked micro-lines */}
          <div className="sm:hidden flex flex-col items-center text-[#4a5568] text-base">
            <span className="mb-2">They were too late.</span>
            <span className="mb-2">Not top of mind.</span>
            <span className="mb-5">Never in the conversation at all.</span>
            <strong className="text-navy mb-2">Not because of effort &mdash;</strong>
            <strong className="text-navy">because of timing.</strong>
          </div>

          {/* Desktop — original prose */}
          <p className="hidden sm:block text-[#4a5568] text-base leading-relaxed" style={{ lineHeight: "1.65" }}>
            Most title companies do not lose closings because of effort. They lose because they were too late, not top of mind, or never in the conversation at all.
          </p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 md:mb-12">
          {[
            {
              icon: "ri-group-line",
              stat: "238,000+",
              label: "Active Agents in the Market",
              note: "The opportunity is massive",
              color: "#19a2fd",
            },
            {
              icon: "ri-calendar-2-line",
              stat: "476 days",
              label: "To reach top agents manually",
              note: "At 100 dials per day — impossible",
              color: "#f1663a",
            },
            {
              icon: "ri-rocket-2-line",
              stat: "Same Day",
              label: "AI outreach timing",
              note: "Triggered the moment listings go live",
              color: "#0b1f33",
            },
            {
              icon: "ri-money-dollar-circle-line",
              stat: "15",
              label: "Closings to cover the platform",
              note: "Everything beyond = pure upside",
              color: "#f1663a",
            },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-2xl border border-gray-100 px-6 py-6">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: card.color + "15" }}
              >
                <i className={`${card.icon} text-lg`} style={{ color: card.color }} />
              </div>
              <p className="text-navy font-extrabold text-2xl leading-none mb-1">{card.stat}</p>
              <p className="text-[#4a5568] text-sm font-medium leading-snug mb-1">{card.label}</p>
              <p className="text-xs text-gray-400 italic">{card.note}</p>
            </div>
          ))}
        </div>

        {/* Highlight box */}
        <div className="rounded-2xl bg-white border border-primary/20 px-5 md:px-8 py-5 md:py-7 flex items-start gap-4 md:gap-5 mb-5">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-fire-line text-primary text-lg" />
          </div>
          <div>
            <h3 className="text-navy font-bold text-base mb-2">You only need a handful of additional closings each month to justify the platform.</h3>
            <p className="text-[#4a5568] text-sm leading-relaxed mb-4">
              In this webinar, we walk through the exact math — what it takes to break even, and what the upside looks like once AI is doing the heavy lifting on outreach.
            </p>
            <button
              onClick={onRegister}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary hover:bg-orange-600 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Join the Webinar
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </span>
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-400 text-center leading-relaxed">
          Illustrative examples only. Results vary based on usage, market conditions, and execution.
        </p>
      </div>
    </section>
  );
}
